import styled, { keyframes } from 'styled-components';

import { ACQ, ANIM } from '../../../../theme/tokens';
import { Text } from '../../../../components';
import { TabButton, TabDot, TabScroll } from './Campaigns.styles';

/**
 * The browser chrome shared by the acquisition Campaigns browser and the operations
 * Promotions browser: the tab row that sits on the PAGE, and the card beneath it that
 * opens on a single header row (category chips left, search hard right) with the search
 * field on its own full-width row below.
 *
 * It lives in its own module, rather than in `Campaigns.styles`, for two reasons. The
 * card-level names would collide (`Campaigns.styles` already exports `Card`, `HeadRow`
 * and `Divider` for the campaign card itself), and this is genuinely one shared
 * vocabulary: both browsers group identically and differ only in the list they put
 * inside the card, so a retune belongs in one place instead of two.
 *
 * Who renders the tab strip, and how far the shared part reaches: `TabsBar` +
 * `BrowserTabScroll` + `BrowserTab` are the ONE page-level strip, rendered by both campaign
 * browsers AND by the wallet's activity/invoices strip (`pages/wallet`), which dropped its
 * own in-panel bar to join them. A retune of those three therefore lands on three pages at
 * once, which is the point. What does NOT reach the wallet: `LiveTabDot`, and the card and
 * search/category vocabulary below, none of which the wallet renders. `TabCount` is NOT
 * extended here, all three import it straight from `Campaigns.styles`.
 *
 * Note the wallet keeps `role="tablist"`/`role="tab"` on these while the campaign browsers
 * use `aria-pressed` toggles: the wallet strip drives a real `tabpanel` and the browsers do
 * not, so the semantics belong to the consumer, not to the styled components.
 */

/** Wraps the whole browser (tab row + card) so the two stack as one block. */
export const Browser = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Scrolled to after an activation, and by the acquisition hero's "Activate a new
     campaign" CTA; stop clear of the overlay header (PageShell scrim ~112px) and keep
     the section heading above it in view. */
  scroll-margin-top: 150px;
`;

/**
 * The tab row, on the page background rather than inside a card: out here the active tab's
 * pill is the only filled shape, so the row reads as a set of page-level switches rather
 * than a strip inside a panel.
 *
 * It carries no width. In a flex column that stretches its children (the `Browser` below)
 * that is already full width; a consumer whose column aligns to flex-start has to give it
 * one, or `BrowserTabScroll` has no width to scroll within and the tabs overflow the page
 * instead (`pages/wallet` sets it through `.activity-tabs`).
 */
export const TabsBar = styled.div`
  display: flex;
  align-items: center;
  /* Exactly the tab's own height: no strip to pad out here on the page, so the row hugs
     the tabs. */
  height: 48px;
  padding: 0 2px;
  overflow: hidden;
`;

/**
 * Tab metrics from Figma node 9492:533. `TabButton`, the base this extends, is no longer
 * rendered on its own anywhere: it holds the geometry every strip shares and this subclass
 * is the only skin, so its own `$active` fill and padding never reach the screen.
 *
 * Every tab is padded 16px, selected or not. The active state used to get 16px against
 * the resting 8px, but that made the row reflow on each switch: the label a pointer was
 * about to hit moved sideways as the previous tab shed its padding. One inset for both
 * states keeps the tabs geometrically still and only the fill changes. Everything else
 * already matched (48px tall, 12px radius, 8px internal gap).
 */
export const BrowserTab = styled(TabButton)`
  padding: 0 16px;
  /* Out on the page the shared tabActiveBg no longer works (see the token note):
     use the page-level pair, and back the fill with a hairline and a weight bump so
     the selected tab does not depend on one low-contrast fill to be legible. */
  background: ${(p) => (p.$active ? ACQ.tabPageActiveBg : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? ACQ.tabPageActiveBorder : 'transparent')};
  font-weight: ${(p) => (p.$active ? 500 : 400)};
`;

/** 8px between tabs, per the same node (the `TabScroll` base it extends uses 4). */
export const BrowserTabScroll = styled(TabScroll)`
  gap: 8px;
`;

/**
 * The card the open view lives in: header row, optional search row, then the list.
 *
 * No border: the surface tint alone separates it from the page. A hairline read as a
 * hard outline around the whole block, most visibly in the light theme where an empty
 * view is a large expanse of near-page-coloured card.
 */
export const BrowserCard = styled.div`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/**
 * The card's single header row. The category chips take the space they need and scroll
 * if there are more than fit; the search sits hard right.
 */
export const BrowserHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 6px 5px;
`;

/** The "Category" label leading the chip row. */
export const FilterLbl = styled(Text)`
  font-size: 12px;
  color: ${ACQ.placeholder};
  flex-shrink: 0;
`;

/**
 * Holds the chips, scrolling horizontally when there are more than fit. `min-width: 0`
 * lets it shrink so the search keeps its slot on the right.
 */
export const CategoryChips = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Category chip, from Figma node 9489:2458: 8/16 padding and a 20px radius make a
 * compact rounded pill. Every colour it uses already had an exact ACQ token (the tint,
 * border and orange of the active state; the 4%/10% white of the resting one).
 */
export const CategoryPill = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 20px;
  flex-shrink: 0;
  font-size: 12px;
  line-height: normal;
  cursor: pointer;
  background: ${(p) => (p.$on ? ACQ.orangeTint : ACQ.pillBg)};
  border: 1px solid ${(p) => (p.$on ? ACQ.orangeBorder : ACQ.pillBorder)};
  color: ${(p) => (p.$on ? ACQ.orange : ACQ.t400)};
  transition:
    background-color ${ANIM.INTERACT_MS}ms ease,
    border-color ${ANIM.INTERACT_MS}ms ease,
    color ${ANIM.INTERACT_MS}ms ease;
`;

/**
 * The "Running" tab's status dot, breathing. A variant rather than a change to
 * `TabDot`: that atom is the plain dot primitive, and a heartbeat belongs to a
 * live-count badge specifically, not to every dot that borrows it.
 */
const livePulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.5; transform: scale(0.78); }
`;

export const LiveTabDot = styled(TabDot)`
  animation: ${livePulse} ${ANIM.LIVE_DOT_PULSE_MS}ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Pins the search control to the right edge of the header row. */
export const SearchSlot = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

/**
 * The search control, wearing the same active treatment as a selected category pill.
 * It is a 32px round chip holding just the magnifier (Figma node 9489:2458) and grows
 * to 68px to take a divider and a dismiss when there is a search to drop (Figma node
 * 9492:667). It is ON whenever a term is actually filtering the list, NOT merely while
 * the field is open, so a collapsed search still announces that something is hidden
 * behind it.
 *
 * A container rather than a button, because in the wide state it holds TWO actions and
 * a button cannot nest buttons.
 */
export const SearchControl = styled.div<{ $on: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  /* border-box, so the 32px IS the outer height the node specifies. Without it the
     padding and border stack on top and the chip renders 50px tall.
     The node's 8px padding / 10px gap are redistributed to 4px / 6px so each glyph
     can carry a 24px hit box (WCAG 2.5.8) inside the same 32px-tall, ~68px-wide
     footprint: 4 + 24 + 6 + 1 + 6 + 24 + 4 = 69. */
  box-sizing: border-box;
  gap: 6px;
  height: 32px;
  padding: 0 4px;
  border-radius: 20px;
  flex-shrink: 0;
  background: ${(p) => (p.$on ? ACQ.orangeTint : ACQ.pillBg)};
  border: 1px solid ${(p) => (p.$on ? ACQ.orangeBorder : ACQ.pillBorder)};
  color: ${(p) => (p.$on ? ACQ.orange : ACQ.t400)};
  transition:
    background-color ${ANIM.INTERACT_MS}ms ease,
    border-color ${ANIM.INTERACT_MS}ms ease,
    color ${ANIM.INTERACT_MS}ms ease;
`;

/**
 * Icon button; takes the control's colour so the chip tints as one.
 *
 * The 16px glyph sits in a 24px box, so the target clears the WCAG 2.5.8 minimum while
 * the chip keeps its specified footprint. It matters here more than usual: this button
 * is how search is opened, and its sibling dismiss is the ONLY way to drop a term while
 * the field is hidden, so two 16px targets a few px apart were a mis-tap away from doing
 * the opposite of what was wanted.
 */
export const SearchIconBtn = styled.button`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;

/**
 * Hairline between the two actions, so the chip reads as two distinct hit areas.
 *
 * Deliberately NOT the shared `Divider`. Two reasons, both measured: that component
 * renders `role="separator"` and forwards only variant/orientation/className, so the
 * `aria-hidden` this needs is silently dropped and a decorative rule inside a control
 * gets announced; and it paints `COLORS.hairline`, which is tuned to sit on a card, so
 * over this chip's orange tint it composites to 1.2:1 and disappears, against 6.3:1 for
 * the tier used here. A separator between two buttons is chrome, not a content
 * boundary, so a plain decorative rule is the right primitive.
 */
export const SearchDivider = styled.span`
  width: 1px;
  height: 16px;
  flex-shrink: 0;
  align-self: center;
  background: ${ACQ.t400};
`;

/**
 * Drops the search entirely. Deliberately NOT tinted with the chip: it is the way out
 * of the active state, not part of it, so it stays neutral against the orange.
 */
export const SearchDismiss = styled(SearchIconBtn)`
  color: ${ACQ.t50};
`;

/**
 * The search field gets its own full-width row under the header rather than a box that
 * grows sideways out of it: the toggle only shows and hides this row, so the category
 * chips never reflow to make room for it. The row holds the input pill and, beside it,
 * the live result count.
 */
export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0 5px;
`;

/** The input itself: a pill, so it reads as the same family as the chips above it. */
export const SearchPill = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  padding: 0 6px 0 16px;
  border-radius: 9999px;
  background: ${ACQ.focusBg};
  border: 1px solid ${ACQ.focusBorder};
  color: ${ACQ.t400};
`;

export const SearchField = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  background: transparent;
  border: 0;
  outline: 0;
  font-size: 14px;
  color: ${ACQ.t50};
  &::placeholder {
    color: ${ACQ.placeholder};
  }
`;

/** Empties the text but keeps the field open for the next attempt. */
export const SearchClear = styled.button`
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  margin-right: 5px;
  background: ${ACQ.searchClearBg};
  border: 0;
  border-radius: 50%;
  color: ${ACQ.t400};
  cursor: pointer;
  /* The two properties that actually change on hover, on the same interaction token as
     the chips and the search control beside it (the 0.18s literal this carried over
     from the retired toolbar was both untokenised and a lone odd duration). */
  transition:
    background-color ${ANIM.INTERACT_MS}ms ease,
    color ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    color: ${ACQ.t50};
    background: ${ACQ.searchClearHover};
  }
`;

/**
 * How many rows the term matched, OUTSIDE the pill to its right. This is the size of
 * the whole match set, NOT of the page currently rendered: with more matches than the
 * browser's page size the count runs ahead of the visible cards and "Load more" reveals
 * the rest. It is counted off the same array the list slices, so "0 results" and the
 * "No matches" empty state can never disagree.
 */
export const SearchResults = styled(Text)`
  flex-shrink: 0;
  font-size: 14px;
  color: ${ACQ.t400};
  white-space: nowrap;
`;
